import {
	forwardRef,
	useCallback,
	useEffect,
	useId,
	useImperativeHandle,
	useRef,
	useState,
	type MouseEvent,
	type ReactElement,
	type ReactNode,
	type Ref,
	type RefObject,
} from 'react';
import { Box, Flex, Stack, Text, type BoxProps, type FlexProps } from '@chakra-ui/react';
import { Card } from '../primitives/Card';
import { Icon } from '../primitives/Icon';
import { IconButton } from '../primitives/Button';
import { UnderlineTabs } from '../primitives/UnderlineTabs';

/**
 * TabbedSections — a long form split into rounded, collapsible section boxes
 * with a sticky UnderlineTabs bar that tracks (scroll-spy) and navigates to
 * them. Built for drawer bodies but works in any scroll container.
 *
 * - Sections start expanded; each header has a chevron to collapse it.
 * - Clicking a tab expands that section, collapses every other one, then
 *   smooth-scrolls to it below the sticky bar.
 * - The ref handle lets callers reveal a section or a field inside one (e.g.
 *   a "missing field" link that expands, scrolls, then focuses).
 * - The scroll container is the nearest scrollable ancestor unless
 *   `scrollContainerRef` is passed.
 *
 * ```tsx
 * const sectionsRef = useRef<TabbedSectionsHandle<'details' | 'cost'>>(null);
 *
 * <TabbedSections
 *   ref={sectionsRef}
 *   header={<SummaryCard />}
 *   stickyProps={{ mx: -5 }}
 *   tabsProps={{ px: 5 }}
 *   sections={[
 *     { id: 'details', tabLabel: 'Details', title: 'Part Details', content: <DetailsFields /> },
 *     { id: 'cost', tabLabel: 'Cost', title: 'Cost And Restocking', content: <CostFields /> },
 *   ]}
 * />
 *
 * await sectionsRef.current?.revealElement(fieldEl);
 * fieldEl.querySelector('input')?.focus({ preventScroll: true });
 * ```
 */
export interface TabbedSection<T extends string = string> {
	id: T;
	tabLabel: string;
	title: ReactNode;
	/** Secondary line under the title (e.g. a summary of what's inside). */
	summary?: ReactNode;
	/** Header content left of the chevron (actions, badges). */
	headerRight?: ReactNode;
	content: ReactNode;
}

export interface TabbedSectionsHandle<T extends string = string> {
	/** Expand `id`, collapse the other sections, then smooth-scroll to `id`. */
	scrollToSection: (id: T) => Promise<void>;
	/** Expand one section without animation; resolves once layout settles. */
	expandSection: (id: T) => Promise<void>;
	/** Expand the section containing `element`, then scroll it into view. */
	revealElement: (element: Element) => Promise<void>;
	/** The resolved scroll container, once mounted. */
	getScrollContainer: () => HTMLElement | null;
}

export interface TabbedSectionsProps<T extends string = string> {
	sections: ReadonlyArray<TabbedSection<T>>;
	/** Content above the tab bar; scrolls away while the bar sticks. */
	header?: ReactNode;
	/** Defaults to the nearest scrollable ancestor. */
	scrollContainerRef?: RefObject<HTMLElement | null>;
	/** Offset (px) for the sticky bar inside the scroll container. */
	stickyTop?: number;
	/** Props for the sticky wrapper (bleed with `mx`, change `bg`). */
	stickyProps?: BoxProps;
	/** Props for the UnderlineTabs hairline row (e.g. `px` to match a bleed). */
	tabsProps?: Omit<FlexProps, 'onChange' | 'children'>;
	/** Fires when the active tab changes (scroll-spy or navigation). */
	onActiveSectionChange?: (id: T) => void;
	/** Gap between section boxes and around the tab bar (Chakra space). */
	spacing?: number;
}

const COLLAPSE_MS = 200;
/** How far past the sticky bar a section's top may sit and still count as active. */
const SPY_SLOP_PX = 24;
const NAV_GAP_PX = 12;

const nextFrame = () => new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

const findScrollParent = (el: HTMLElement | null): HTMLElement | null => {
	let node = el?.parentElement ?? null;
	while (node) {
		const { overflowY } = window.getComputedStyle(node);
		if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') return node;
		node = node.parentElement;
	}
	return (document.scrollingElement as HTMLElement | null) ?? null;
};

function TabbedSectionsInner<T extends string = string>(
	{
		sections,
		header,
		scrollContainerRef,
		stickyTop = 0,
		stickyProps,
		tabsProps,
		onActiveSectionChange,
		spacing = 4,
	}: TabbedSectionsProps<T>,
	ref: Ref<TabbedSectionsHandle<T>>,
) {
	const instanceId = useId();
	const rootRef = useRef<HTMLDivElement>(null);
	const stickyRef = useRef<HTMLDivElement>(null);
	const sectionEls = useRef(new Map<T, HTMLElement>());
	const navLock = useRef<{ timer: ReturnType<typeof setTimeout> | null; locked: boolean }>({
		timer: null,
		locked: false,
	});
	const [activeId, setActiveIdState] = useState<T | undefined>(sections[0]?.id);
	const [collapsed, setCollapsed] = useState<ReadonlySet<T>>(() => new Set());
	const collapsedRef = useRef(collapsed);
	const [instant, setInstant] = useState(false);

	const onActiveRef = useRef(onActiveSectionChange);
	onActiveRef.current = onActiveSectionChange;
	const activeRef = useRef(activeId);
	const setActiveId = useCallback((id: T) => {
		if (activeRef.current === id) return;
		activeRef.current = id;
		setActiveIdState(id);
		onActiveRef.current?.(id);
	}, []);

	const getScrollContainer = useCallback(
		() => scrollContainerRef?.current ?? findScrollParent(rootRef.current),
		[scrollContainerRef],
	);

	const stickyOffset = useCallback(
		() => stickyTop + (stickyRef.current?.offsetHeight ?? 0),
		[stickyTop],
	);

	const sectionIds = sections.map((s) => s.id).join('|');

	useEffect(() => {
		const container = getScrollContainer();
		if (!container) return;
		const ids = sectionIds.split('|') as T[];
		let frame = 0;
		const spy = () => {
			frame = 0;
			if (navLock.current.locked) return;
			const atBottom =
				container.scrollTop + container.clientHeight >= container.scrollHeight - 2;
			if (atBottom && ids.length > 0) {
				setActiveId(ids[ids.length - 1]);
				return;
			}
			const top = container.getBoundingClientRect().top + stickyOffset() + SPY_SLOP_PX;
			let current = ids[0];
			for (const id of ids) {
				const el = sectionEls.current.get(id);
				if (el && el.getBoundingClientRect().top <= top) current = id;
			}
			if (current) setActiveId(current);
		};
		const onScroll = () => {
			if (navLock.current.locked) {
				// Release the lock once programmatic scrolling goes quiet.
				if (navLock.current.timer) clearTimeout(navLock.current.timer);
				navLock.current.timer = setTimeout(() => {
					navLock.current.locked = false;
				}, 150);
				return;
			}
			if (!frame) frame = requestAnimationFrame(spy);
		};
		container.addEventListener('scroll', onScroll, { passive: true });
		return () => {
			container.removeEventListener('scroll', onScroll);
			if (frame) cancelAnimationFrame(frame);
		};
	}, [getScrollContainer, sectionIds, setActiveId, stickyOffset]);

	useEffect(
		() => () => {
			if (navLock.current.timer) clearTimeout(navLock.current.timer);
		},
		[],
	);

	const updateCollapsed = useCallback((next: ReadonlySet<T>) => {
		collapsedRef.current = next;
		setCollapsed(next);
	}, []);

	/** Apply an expand without the collapse animation, then wait for layout. */
	const expandInstantly = useCallback(
		async (ids: ReadonlyArray<T> | 'all') => {
			const prev = collapsedRef.current;
			const next = new Set(prev);
			for (const id of ids === 'all' ? Array.from(prev) : ids) next.delete(id);
			if (next.size === prev.size) return;
			setInstant(true);
			updateCollapsed(next);
			await nextFrame();
			await nextFrame();
			setInstant(false);
		},
		[updateCollapsed],
	);

	/** Expand only `id` and collapse every other section, without animation. */
	const focusInstantly = useCallback(
		async (id: T) => {
			const prev = collapsedRef.current;
			const next = new Set(sectionIds.split('|') as T[]);
			next.delete(id);
			if (next.size === prev.size && Array.from(next).every((s) => prev.has(s))) return;
			setInstant(true);
			updateCollapsed(next);
			await nextFrame();
			await nextFrame();
			setInstant(false);
		},
		[sectionIds, updateCollapsed],
	);

	const scrollContainerTo = useCallback(
		(top: number) => {
			const container = getScrollContainer();
			container?.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
		},
		[getScrollContainer],
	);

	const scrollToSection = useCallback(
		async (id: T) => {
			setActiveId(id);
			navLock.current.locked = true;
			if (navLock.current.timer) clearTimeout(navLock.current.timer);
			navLock.current.timer = setTimeout(() => {
				navLock.current.locked = false;
			}, 400);
			await focusInstantly(id);
			const container = getScrollContainer();
			const el = sectionEls.current.get(id);
			if (!container || !el) return;
			const offset =
				el.getBoundingClientRect().top -
				container.getBoundingClientRect().top -
				stickyOffset() -
				NAV_GAP_PX;
			scrollContainerTo(container.scrollTop + offset);
		},
		[focusInstantly, getScrollContainer, scrollContainerTo, setActiveId, stickyOffset],
	);

	const expandSection = useCallback((id: T) => expandInstantly([id]), [expandInstantly]);

	const revealElement = useCallback(
		async (element: Element) => {
			const owner = Array.from(sectionEls.current.entries()).find(([, el]) =>
				el.contains(element),
			)?.[0];
			if (owner) await expandInstantly([owner]);
			const container = getScrollContainer();
			if (!container) return;
			const c = container.getBoundingClientRect();
			const r = element.getBoundingClientRect();
			const sticky = stickyOffset();
			const visible = container.clientHeight - sticky;
			const offset = r.top - c.top - sticky - Math.max(0, (visible - r.height) / 2);
			scrollContainerTo(container.scrollTop + offset);
		},
		[expandInstantly, getScrollContainer, scrollContainerTo, stickyOffset],
	);

	useImperativeHandle(
		ref,
		() => ({ scrollToSection, expandSection, revealElement, getScrollContainer }),
		[scrollToSection, expandSection, revealElement, getScrollContainer],
	);

	const toggle = (id: T) => {
		const next = new Set(collapsedRef.current);
		if (!next.delete(id)) next.add(id);
		updateCollapsed(next);
	};

	// Chakra Tabs only fires onChange for a *different* tab; re-clicking the
	// active one should still scroll back to it.
	const handleTabRowClick = (e: MouseEvent<HTMLDivElement>) => {
		const tab = (e.target as HTMLElement).closest<HTMLElement>('[role="tab"]');
		if (!tab || tab.getAttribute('aria-selected') !== 'true') return;
		const tabs = Array.from(e.currentTarget.querySelectorAll('[role="tab"]'));
		const section = sections[tabs.indexOf(tab)];
		if (section) void scrollToSection(section.id);
	};

	return (
		<Box ref={rootRef}>
			{header}
			<Box
				ref={stickyRef}
				position="sticky"
				top={`${stickyTop}px`}
				zIndex={2}
				bg="white"
				mb={spacing}
				onClick={handleTabRowClick}
				{...stickyProps}
			>
				<UnderlineTabs<T>
					items={sections.map((s) => ({ value: s.id, label: s.tabLabel }))}
					value={activeId ?? sections[0]?.id ?? ('' as T)}
					onChange={(id) => void scrollToSection(id)}
					{...tabsProps}
				/>
			</Box>
			<Stack spacing={spacing}>
				{sections.map((section) => {
					const isOpen = !collapsed.has(section.id);
					const bodyId = `${instanceId}-${section.id}`;
					return (
						<Card
							key={section.id}
							ref={(el: HTMLDivElement | null) => {
								if (el) sectionEls.current.set(section.id, el);
								else sectionEls.current.delete(section.id);
							}}
							variant="outline"
							bg="transparent"
							p={0}
						>
							<Flex align="center" gap={3} px={4} py={3}>
								<Box
									flex={1}
									minW={0}
									cursor="pointer"
									onClick={() => toggle(section.id)}
								>
									<Text
										as="div"
										fontSize="md"
										fontWeight="semibold"
										color="fg.default"
									>
										{section.title}
									</Text>
									{section.summary}
								</Box>
								{section.headerRight}
								<IconButton
									aria-label={isOpen ? 'Collapse section' : 'Expand section'}
									aria-expanded={isOpen}
									aria-controls={bodyId}
									variant="ghostGray"
									size="sm"
									icon={
										<Icon
											name="chevronDown"
											size="md"
											transform={isOpen ? 'rotate(180deg)' : undefined}
											transition="transform 0.15s ease"
										/>
									}
									onClick={() => toggle(section.id)}
								/>
							</Flex>
							<Box
								id={bodyId}
								display="grid"
								gridTemplateRows={isOpen ? '1fr' : '0fr'}
								visibility={isOpen ? 'visible' : 'hidden'}
								transition={
									instant
										? 'none'
										: isOpen
											? `grid-template-rows ${COLLAPSE_MS}ms ease, visibility 0s`
											: `grid-template-rows ${COLLAPSE_MS}ms ease, visibility 0s linear ${COLLAPSE_MS}ms`
								}
								sx={{ '@media (prefers-reduced-motion: reduce)': { transition: 'none' } }}
							>
								<Box minH={0} overflow="hidden">
									<Box
										px={4}
										pt={4}
										pb={4}
										borderTopWidth="1px"
										borderColor="border.subtle"
									>
										{section.content}
									</Box>
								</Box>
							</Box>
						</Card>
					);
				})}
			</Stack>
		</Box>
	);
}

export const TabbedSections = forwardRef(TabbedSectionsInner) as <T extends string = string>(
	props: TabbedSectionsProps<T> & { ref?: Ref<TabbedSectionsHandle<T>> },
) => ReactElement;

export default TabbedSections;
